import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormCodeEditor } from '@ipfabric/ui-components/FormCodeEditor/FormCodeEditor';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { IconButton } from '@ipfabric/ui-components/IconButton/IconButton';
import { type ChangeEvent, useRef } from 'react';
import { type UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';
import { generateGuid } from 'utils/guid';
import { useNotifications } from 'utils/hooks/useNotifications';
import {
  type CustomDiscoveryDefinitionRow,
  definitionName,
} from './CustomDiscoveryDefinitionRow';
import { DefinitionCheckStatus } from './DefinitionCheckStatus';

type FieldValues = {
  customDiscoveryDefinitions: CustomDiscoveryDefinitionRow[];
};

type Props = {
  form: UseFormReturn<FieldValues>;
};

const newDefinition = (yaml: string): CustomDiscoveryDefinitionRow => ({
  id: generateGuid(),
  enabled: true,
  yaml,
});

export const CustomDiscoveryDefinitions = ({ form }: Props) => {
  const notifications = useNotifications();
  // DOM handle of the hidden file input opened by the import button
  const fileInputRef = useRef<HTMLInputElement>(null);
  // the definitions keep their own `id`, so the field array keys by another name
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'customDiscoveryDefinitions',
    keyName: 'fieldKey',
  });
  const definitions = useWatch({
    control: form.control,
    name: 'customDiscoveryDefinitions',
  });

  const importFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    try {
      const texts = await Promise.all(files.map((file) => file.text()));
      append(texts.map(newDefinition));
    } catch {
      notifications.showNotification({
        type: 'danger',
        message: 'The file could not be read',
      });
    }
  };

  return (
    <div className='flex flex-col gap'>
      <div className='flex flex-row gap items-center'>
        <p className='flex-1 text-default text-level-6'>
          Each definition adds one or more commands to discovery, or reuses
          outputs IP Fabric already collects, and says how to parse and combine
          them into Custom Discovery Data. Definitions are checked as you type;
          an enabled definition that does not load can't be saved, a disabled
          one is kept as a draft.
        </p>
        <Button
          dataTestId='custom-discovery-definitions-import'
          icon='fa-file-import'
          onClick={() => fileInputRef.current?.click()}
          type='button'
          variant='outline'
        >
          Import YAML file
        </Button>
        <Button
          dataTestId='custom-discovery-definitions-add'
          icon='fa-plus'
          onClick={() => append(newDefinition(''))}
          type='button'
          variant='primary'
        >
          Add definition
        </Button>
        <input
          ref={fileInputRef}
          type='file'
          accept='.yaml,.yml,text/yaml'
          multiple
          hidden
          onChange={importFiles}
        />
      </div>
      {fields.length === 0 && (
        <Card className='p text-default text-level-6'>
          No definitions. Add one, or import a YAML file.
        </Card>
      )}
      {fields.map((field, index) => (
        <Card key={field.fieldKey} className='p flex flex-col gap-2xs'>
          <div className='flex flex-row gap items-center'>
            <div className='flex-1 text-default text-level-5 font-semibold font-heading'>
              {definitionName(definitions[index]?.yaml ?? '') ??
                'Unnamed definition'}
            </div>
            <FormSwitch
              control={form.control}
              name={`customDiscoveryDefinitions.${index}.enabled`}
              label='Enabled'
            />
            <IconButton
              dataTestId={`custom-discovery-definition-${index}-remove`}
              accent='negative'
              icon='fa-trash-can'
              onClick={() => remove(index)}
              type='button'
              variant='plain'
            />
          </div>
          <FormCodeEditor
            control={form.control}
            name={`customDiscoveryDefinitions.${index}.yaml`}
            language='yaml'
            showLineNumbers
            minHeight='12rem'
            maxHeight='36rem'
            placeholder='name: …'
          />
          <DefinitionCheckStatus form={form} index={index} />
        </Card>
      ))}
    </div>
  );
};
